import React, { useEffect, useState } from "react";
import {
	Box,
	CircularProgress,
	IconButton,
	makeStyles,
	Typography,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import WbSunnyIcon from "@material-ui/icons/WbSunny";
import Brightness3Icon from "@material-ui/icons/Brightness3";
import moment from "moment";
import { useSelector } from "react-redux";
import { Get } from "../../../actions";
import { Get_GetTotalReservations_URL } from "../../../constants/apiUrls";
import { format } from "../../../heplers/format";
import { getLocale, getTranslation } from "../../../heplers/translationHelper";

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: "10px",
		width: "100%",
		overflowX: "auto",
		scrollbarWidth: "none",
		"&::-webkit-scrollbar": { display: "none" },
	},
	navBtn: {
		backgroundColor: "#004467",
		color: "#ffffff",
		width: "38px",
		height: "38px",
		flexShrink: 0,
		transition: "background 0.2s",
		"&:hover": { backgroundColor: "#003255" },
		"&:disabled": { backgroundColor: "#c5d0d8", color: "#fff" },
	},
	toggleGroup: {
		display: "flex",
		flex: 1,
		justifyContent: "space-between",
		gap: "8px",
		border: "none !important",
		flexWrap: "nowrap",
		"& .MuiToggleButtonGroup-grouped": {
			border: "none !important",
			borderRadius: "12px !important",
			margin: 0,
			flex: 1,
		},
	},
	dayCard: {
		minWidth: "82px",
		padding: "10px 8px !important",
		borderRadius: "12px !important",
		border: "1.5px solid #dce8f0 !important",
		backgroundColor: "#ffffff !important",
		transition: "all 0.2s ease !important",
		textTransform: "none !important",
		"&:hover": {
			backgroundColor: "#f0f5fa !important",
			borderColor: "#004467 !important",
			transform: "translateY(-2px)",
			boxShadow: "0 4px 14px rgba(0,68,103,0.14) !important",
		},
		"&.Mui-selected": {
			backgroundColor: "#004467 !important",
			borderColor: "#004467 !important",
			boxShadow: "0 4px 14px rgba(0,68,103,0.32) !important",
			transform: "translateY(-2px)",
		},
		"&.Mui-selected:hover": {
			backgroundColor: "#003255 !important",
		},
	},
}));

export const DaySelection = ({ selectedDate, setSelectedDate }) => {
	const [weekDates, setWeekDates] = useState([]);
	const [loadingWeekDays, setLoadingWeekDays] = useState(false);
	const classes = useStyles();

	// show the week (Monday to Sunday) of the selected day, also when the page sets a day outside the shown week
	useEffect(() => {
		if (weekDates.some((date) => format("date", date) === selectedDate)) return;
		const day = moment(selectedDate, "DD-MM-YYYY");
		const base = day.isValid() ? day.toDate() : new Date();
		const d = base.getDay();
		const startOfWeek = new Date(base.valueOf() - (d === 0 ? 6 : d - 1) * 86400000);
		const arr = [];
		const cursor = new Date(startOfWeek);
		for (let i = 0; i < 7; i++) {
			arr.push(cursor.toISOString());
			cursor.setDate(cursor.getDate() + 1);
		}
		setWeekDates(arr);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedDate]);

	const handlePrev = () => {
		setLoadingWeekDays(true);
		const first = new Date(weekDates[0]);
		first.setDate(first.getDate() - 7);
		const arr = [];
		for (let i = 0; i < 7; i++) {
			arr.push(new Date(first).toISOString());
			first.setDate(first.getDate() + 1);
		}
		setWeekDates(arr);
		setLoadingWeekDays(false);
	};

	const handleNext = () => {
		setLoadingWeekDays(true);
		const last = new Date(weekDates[6]);
		last.setDate(last.getDate() + 1);
		const arr = [];
		for (let i = 0; i < 7; i++) {
			arr.push(new Date(last).toISOString());
			last.setDate(last.getDate() + 1);
		}
		setWeekDates(arr);
		setLoadingWeekDays(false);
	};

	return (
		<Box className={classes.root}>
			<IconButton className={classes.navBtn} onClick={handlePrev} disabled={loadingWeekDays} size="small">
				<ChevronLeftIcon fontSize="small" />
			</IconButton>

			<ToggleButtonGroup
				exclusive
				value={selectedDate}
				onChange={(e, value) => { if (value) setSelectedDate(value); }}
				className={classes.toggleGroup}
			>
				{weekDates.map((date) => (
					<ToggleButton key={date} value={format("date", date)} className={classes.dayCard}>
						<DayCard date={date} isSelected={selectedDate === format("date", date)} />
					</ToggleButton>
				))}
			</ToggleButtonGroup>

			<IconButton className={classes.navBtn} onClick={handleNext} disabled={loadingWeekDays} size="small">
				<ChevronRightIcon fontSize="small" />
			</IconButton>
		</Box>
	);
};

const DayCard = ({ date, isSelected }) => {
	const [loading, setLoading] = useState(false);
	const [morning, setMorning] = useState(0);
	const [evening, setEvening] = useState(0);
	const { lastUpdateTime } = useSelector((state) => state.userReservation);
	const isToday = moment(date).isSame(moment(), "day");

	useEffect(() => {
		setLoading(true);
		Get(
			{ date: moment(date).format("MM-DD-YYYY"), dayTimeType: 0 },
			Get_GetTotalReservations_URL,
			null,
			(resp) => {
				setMorning(resp?.data?.morningTotalReservationsCount ?? 0);
				setEvening(resp?.data?.eveningTotalReservationsCount ?? 0);
				setLoading(false);
			},
			() => setLoading(false)
		);
	}, [date, lastUpdateTime]);

	const textColor = isSelected ? "rgba(255,255,255,0.85)" : "#8a9bb0";
	const strongColor = isSelected ? "#ffffff" : "#004467";

	return (
		<Box display="flex" flexDirection="column" alignItems="center" style={{ minWidth: "66px" }}>
			{/* Day name */}
			<Typography style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.8px", color: textColor, textTransform: "uppercase", lineHeight: 1 }}>
				{new Date(date).toLocaleDateString(getLocale(), { weekday: "short" })}
			</Typography>

			{/* Date number */}
			<Typography style={{ fontSize: "22px", fontWeight: 800, color: strongColor, lineHeight: 1.15, margin: "3px 0 1px" }}>
				{moment(date).format("DD")}
			</Typography>

			{/* Month + year */}
			<Typography style={{ fontSize: "9px", color: textColor, lineHeight: 1, marginBottom: "6px" }}>
				{new Date(date).toLocaleDateString(getLocale(), { month: "short", year: "numeric" })}
			</Typography>

			{/* Today indicator */}
			{isToday && (
				<Box style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: isSelected ? "#FA8900" : "#FA8900", marginBottom: "5px" }} />
			)}

			{/* Counts */}
			{loading ? (
				<CircularProgress size={12} style={{ color: isSelected ? "#ffffff" : "#004467" }} />
			) : (
				<Box display="flex" justifyContent="center" style={{ gap: "8px" }}>
					<Box display="flex" alignItems="center" style={{ gap: "2px" }}>
						<WbSunnyIcon style={{ fontSize: 10, color: isSelected ? "#FA8900" : "#FA8900" }} />
						<Typography style={{ fontSize: "11px", fontWeight: 700, color: strongColor, lineHeight: 1 }}>
							{morning}
						</Typography>
					</Box>
					<Box display="flex" alignItems="center" style={{ gap: "2px" }}>
						<Brightness3Icon style={{ fontSize: 10, color: isSelected ? "rgba(255,255,255,0.6)" : "#90a4ae" }} />
						<Typography style={{ fontSize: "11px", fontWeight: 700, color: strongColor, lineHeight: 1 }}>
							{evening}
						</Typography>
					</Box>
				</Box>
			)}
		</Box>
	);
};
